import { useQuery } from '@tanstack/react-query';
import { channelWa } from '@repletoryreach/sdk-api';

/*
 * Who Meta bills for a number (5 October 2026).
 *
 * A number on our shared WhatsApp account is paid in credits, and a refusal
 * over billing on it is ours to fix. A number on the business's own WhatsApp
 * account (Growth and Scale) is billed by Meta directly, so the same refusal
 * is the business's to fix, in Meta's payment settings. The wording differs,
 * so every page that explains such a refusal needs to know which it is.
 */

/** Where a payment method goes on a WhatsApp Business Account. */
export const META_PAYMENTS_URL = 'https://business.facebook.com/billing_hub/payment_settings';

/** Where a business with its own WhatsApp account manages its numbers, including the display name. */
export const META_NUMBERS_URL = 'https://business.facebook.com/wa/manage/phone-numbers/';

/**
 * The organisation's numbers, read through the same cached query the Numbers
 * page uses, and the answers pages need from them. Never throws and never
 * blocks a page: while the list loads every number reads as ours.
 */
export function useMetaBilling() {
  const q = useQuery({ queryKey: ['phone-numbers'], queryFn: () => channelWa.listPhoneNumbers(), staleTime: 60_000 });
  const rows = q.data?.data ?? [];
  const payerOf = (numberId?: string | null): channelWa.MetaBillingPayer | undefined =>
    numberId ? rows.find((n) => n.id === numberId)?.meta_billing_payer : undefined;
  const ownNumbers = rows.filter((n) => n.meta_billing_payer === 'customer');
  return {
    payerOf,
    /** At least one number is on the business's own WhatsApp account. */
    hasOwnAccount: ownNumbers.length > 0,
    /** Every number is on the business's own WhatsApp account. */
    onlyOwnAccount: rows.length > 0 && ownNumbers.length === rows.length,
    ownAccountAllowed: q.data?.meta?.own_account?.allowed ?? true
  };
}
